import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ACCOUNT_COLORS } from '@/constants/residents/mobile/accountColors';

interface AccountEmptyStateProps {
  /**
   * Icon element to display (typically an SVG component)
   * @example <EmptyCardIcon width={64} height={64} color={ACCOUNT_COLORS.textSecondary} />
   */
  icon: React.ReactNode;
  /**
   * Title text
   */
  title: string;
  /**
   * Description/message text
   */
  message: string;
  /**
   * Size variant - affects title size
   * 'default' = 16px, 'large' = 18px
   */
  variant?: 'default' | 'large';
  /**
   * Maximum width for the message text (optional)
   */
  messageMaxWidth?: number;
}

/**
 * Reusable Empty State Component for Account Settings Sections
 * 
 * Based on Figma designs for:
 * - Payment Methods empty state (large variant)
 * - Autopay empty state (default variant)
 * 
 * @example
 * ```tsx
 * <AccountEmptyState
 *   icon={<EmptyCardIcon width={64} height={64} color={ACCOUNT_COLORS.textSecondary} />}
 *   title={strings.account.paymentMethodsEmptyTitle}
 *   message={strings.account.paymentMethodsEmptyMessage}
 *   variant="large"
 * />
 * ```
 */
export const AccountEmptyState: React.FC<AccountEmptyStateProps> = ({
  icon,
  title,
  message,
  variant = 'default',
  messageMaxWidth,
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        {icon}
      </View>
      <Text style={[
        styles.title,
        variant === 'large' && styles.titleLarge,
      ]}>
        {title}
      </Text>
      <Text style={[
        styles.message,
        messageMaxWidth ? { maxWidth: messageMaxWidth } : null,
      ]}>
        {message}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 36,
    paddingHorizontal: 20,
    gap: 18,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Figma: text-[16px], font-semibold, text-[#eef1f4]
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: ACCOUNT_COLORS.textPrimary,
    textAlign: 'center',
  },
  // Figma: text-[18px] for large variant
  titleLarge: {
    fontSize: 18,
  },
  // Figma: text-[14px], font-normal, text-[#919191]
  message: {
    fontSize: 14,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    textAlign: 'center',
    lineHeight: 21,
  },
});

export default AccountEmptyState;
